import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CHANNEL_LABEL, elapsedShort, type Grade, type ReviveRowDto } from '@coachrex/shared';
import { useRevive, useReviveLead, useSkipRevive, useUndoRescue, useUnskipRevive } from '../lib/api';
import { matchesRep, useRepFilter } from '../lib/repFilter';
import { money } from '../lib/format';
import { Modal } from '../components/Modal';
import { useToast } from '../components/Toast';
import { Chip, ErrorBox, GradeBadge, PageHeader, Pills, RepFilter, Segmented, Skeleton } from '../components/ui';
import './rescue.css';
import './conversations.css';
import './revive.css';

// Good leads that never booked and went quiet. Rescue covers the last 72 hours; this covers everything older.

type Age = 'weeks' | 'months' | 'older' | 'all';
type Grades = 'ab' | 'a' | 'abc';
type Sort = 'best' | 'value' | 'recent';

const DAY = 86_400_000;
const AGES: { value: Age; label: string; test: (days: number) => boolean }[] = [
  { value: 'weeks', label: '2–4 weeks', test: (d) => d < 30 },
  { value: 'months', label: '1–3 months', test: (d) => d >= 30 && d < 90 },
  { value: 'older', label: '3+ months', test: (d) => d >= 90 },
  { value: 'all', label: 'All', test: () => true },
];
const GRADES: Record<Grades, { label: string; grades: Grade[] }> = {
  ab: { label: 'Grades A & B', grades: ['A', 'B'] },
  a: { label: 'Grade A only', grades: ['A'] },
  abc: { label: 'Grades A–C', grades: ['A', 'B', 'C'] },
};
/** Same weighting as Rescue's "Best chance first": value × grade weight. */
const WEIGHT: Record<Grade, number> = { A: 1, B: 0.75, C: 0.45, D: 0.15 };
const chance = (r: ReviveRowDto) => (r.value && r.value > 0 ? r.value : 1) * WEIGHT[r.grade];
const SORTS: Record<Sort, (a: ReviveRowDto, b: ReviveRowDto) => number> = {
  best: (a, b) => chance(b) - chance(a) || Date.parse(b.quietSince) - Date.parse(a.quietSince),
  value: (a, b) => (b.value ?? 0) - (a.value ?? 0),
  recent: (a, b) => Date.parse(b.quietSince) - Date.parse(a.quietSince),
};
const PAGE = 50;
const quietDays = (r: ReviveRowDto) => (Date.now() - Date.parse(r.quietSince)) / DAY;

export function Revive() {
  const { data, error, isLoading } = useRevive();
  const rep = useRepFilter();
  const [params, setParams] = useSearchParams();
  const age = (AGES.some((a) => a.value === params.get('age')) ? params.get('age') : 'all') as Age;
  const grades = (params.get('grades') && params.get('grades')! in GRADES ? params.get('grades') : 'abc') as Grades;
  const sort = (params.get('sort') && params.get('sort')! in SORTS ? params.get('sort') : 'best') as Sort;
  const setParam = (k: string, v: string, dflt: string) => {
    const next = new URLSearchParams(params);
    if (v === dflt) next.delete(k);
    else next.set(k, v);
    setParams(next, { replace: true });
    setShown(PAGE);
  };
  const [shown, setShown] = useState(PAGE);
  const [skipping, setSkipping] = useState<ReviveRowDto | null>(null);
  const revive = useReviveLead();
  const undo = useUndoRescue();
  const toast = useToast();

  // Rep and grade narrow everything on the page; the age pills count within that.
  const scoped = useMemo(
    () => (data?.rows ?? []).filter((r) => matchesRep(rep.value, r.rep?.id) && GRADES[grades].grades.includes(r.grade)),
    [data, rep.value, grades],
  );
  const rows = useMemo(() => {
    const test = AGES.find((a) => a.value === age)!.test;
    return scoped.filter((r) => test(quietDays(r))).sort(SORTS[sort]);
  }, [scoped, age, sort]);

  const currency = data?.currency ?? 'USD';
  const pipeline = rows.reduce((a, r) => a + (r.value ?? 0), 0);

  const send = (r: ReviveRowDto) => {
    if (revive.isPending) return;
    revive.mutate(
      { leadId: r.leadId, note: reviveNote(r) },
      {
        onSuccess: ({ undoId }) => toast({ text: `${first(r.leadName)} is in the rescue queue. A note was added in your CRM.`, undo: () => undo.mutateAsync(undoId) }),
        onError: (e) => toast({ text: e instanceof Error ? e.message : 'Couldn’t revive this lead' }),
      },
    );
  };

  return (
    <div className="page">
      <PageHeader
        title="Revive"
        subtitle={`Good leads that never booked and went quiet: nobody has called, texted or emailed either way for ${data?.quietDays ?? 14}+ days. Revive sends one to the Rescue queue with a note in your CRM, so it gets worked and counts as Recovered when it books.`}
      />
      <div className="stat-cards">
        <Stat label="Worth another try" value={data ? `${rows.length.toLocaleString()} lead${rows.length === 1 ? '' : 's'}` : null} hint={data ? `${GRADES[grades].label}, quiet ${age === 'all' ? `${data.quietDays}+ days` : AGES.find((a) => a.value === age)!.label}` : undefined} />
        <Stat label="Pipeline value" value={data ? money(pipeline, currency) : null} hint={data && rows.length && !rows.some((r) => r.value) ? 'No opportunity values on these leads in your CRM yet.' : undefined} />
        <Stat
          label="Revived, last 30 days"
          value={data ? String(data.revived.count) : null}
          hint={data ? `${data.revived.recovered} recovered${data.revived.recoveredValue ? ` · ${money(data.revived.recoveredValue, currency)}` : ''}` : undefined}
        />
      </div>

      <div className="rescue-tools">
        <Pills
          options={AGES.map((a) => ({ value: a.value, label: a.label, count: scoped.filter((r) => a.test(quietDays(r))).length }))}
          value={age}
          onChange={(v) => setParam('age', v, 'all')}
        />
        <span className="rescue-tools__right">
          <select aria-label="Grades" className={`select-pill${grades !== 'abc' ? ' is-set' : ''}`} value={grades} onChange={(e) => setParam('grades', e.target.value, 'abc')}>
            {(Object.keys(GRADES) as Grades[]).map((g) => (
              <option key={g} value={g}>
                {GRADES[g].label}
              </option>
            ))}
          </select>
          <RepFilter reps={data?.reps ?? []} />
          <Segmented
            label="Sort"
            options={[
              { value: 'best', label: 'Best chance' },
              { value: 'value', label: 'Highest value' },
              { value: 'recent', label: 'Most recent' },
            ]}
            value={sort}
            onChange={(v) => setParam('sort', v, 'best')}
          />
        </span>
      </div>

      <section className="card card--list">
        <div className="list-head revive-grid">
          <span>Lead</span>
          <span>Why it’s worth another try</span>
          <span style={{ textAlign: 'right' }}>Value</span>
          <span style={{ textAlign: 'right' }}>Quiet</span>
          <span />
        </div>
        {error ? (
          <div style={{ padding: 12 }}>
            <ErrorBox error={error} />
          </div>
        ) : isLoading ? (
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} height={64} />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="list-empty">
            {data?.rows.length ? 'Nothing matches these filters.' : 'No good leads have gone quiet. Leads show up here once they are graded and nobody has been in touch for two weeks.'}
          </div>
        ) : (
          <>
            {rows.slice(0, shown).map((r) => (
              <ReviveRow key={r.leadId} r={r} currency={currency} busy={revive.isPending && revive.variables?.leadId === r.leadId} onRevive={send} onSkip={setSkipping} />
            ))}
            {rows.length > shown && (
              <div className="revive-more">
                <button className="btn btn--secondary btn--sm" onClick={() => setShown((n) => n + PAGE)}>
                  Show {Math.min(PAGE, rows.length - shown)} more
                </button>
                <span className="card-meta">
                  {shown} of {rows.length.toLocaleString()}
                </span>
              </div>
            )}
          </>
        )}
      </section>
      {skipping && <SkipModal r={skipping} onClose={() => setSkipping(null)} />}
    </div>
  );
}

function ReviveRow({ r, currency, busy, onRevive, onSkip }: { r: ReviveRowDto; currency: string; busy: boolean; onRevive: (r: ReviveRowDto) => void; onSkip: (r: ReviveRowDto) => void }) {
  return (
    <div className="list-row revive-grid">
      <Link to={`/conversations/${r.leadId}`} className="convo-lead" style={{ color: 'inherit' }}>
        <GradeBadge grade={r.grade} size={26} />
        <span className="convo-lead__text">
          <span className="convo-lead__name">{r.leadName}</span>
          <span className="convo-lead__rep">with {r.rep?.name ?? 'Nobody assigned'}</span>
        </span>
      </Link>
      <span className="rescue-why">
        <span className="revive-reason">{r.reason ?? (r.missing.length ? `Not confirmed yet: ${r.missing.join(', ').toLowerCase()}.` : 'Graded from their calls, texts and emails.')}</span>
        {(r.milestone || r.objection || (r.reason && r.missing.length > 0)) && (
          <span className="revive-tags">
            {r.reason && r.missing.length > 0 && (
              <Chip tone="grey" size="sm">
                Ask about: {r.missing.join(', ').toLowerCase()}
              </Chip>
            )}
            {r.milestone && (
              <Chip tone="blue" size="sm">
                Reached {r.milestone}
              </Chip>
            )}
            {r.objection && (
              <Chip tone="amber" size="sm">
                {r.objection}
              </Chip>
            )}
          </span>
        )}
        {r.lastSaid && (
          <span className="rescue-why__meta">
            <span className="chan-chip">{CHANNEL_LABEL[r.lastSaid.channel]}</span>
            <span title={r.lastSaid.text ?? undefined}>
              {r.lastSaid.text && r.lastSaid.channel !== 'call' ? `“${r.lastSaid.text}”` : r.lastSaid.channel === 'call' ? 'Their last call' : 'Their last message'} ·{' '}
              {elapsedShort(Date.now() - Date.parse(r.lastSaid.at))} ago
            </span>
          </span>
        )}
      </span>
      <span className="num" style={{ fontWeight: 600 }}>
        {money(r.value, currency)}
      </span>
      <span className="num" style={{ color: 'var(--faint)', fontSize: 12.5 }}>
        {elapsedShort(Date.now() - Date.parse(r.quietSince))}
      </span>
      <span className="rescue-actions">
        <button className="btn btn--sm btn--accent" disabled={busy} onClick={() => onRevive(r)}>
          {busy ? 'Reviving…' : 'Revive'}
        </button>
        <button className="link-btn" onClick={() => onSkip(r)}>
          Skip
        </button>
      </span>
    </div>
  );
}

const SKIP_REASONS = ['Not interested', 'Went with someone else', 'Not the right fit', 'Bad timing, try later', 'Can’t reach them'];

function SkipModal({ r, onClose }: { r: ReviveRowDto; onClose: () => void }) {
  const skip = useSkipRevive();
  const unskip = useUnskipRevive();
  const toast = useToast();
  const [reason, setReason] = useState<string | null>(null);
  const [other, setOther] = useState('');
  const text = reason === 'Other' ? other.trim() : reason;
  const save = () => {
    if (!text || skip.isPending) return;
    skip.mutate(
      { leadId: r.leadId, reason: text },
      {
        onSuccess: () => {
          onClose();
          toast({ text: `Skipped ${first(r.leadName)}`, undo: () => unskip.mutateAsync(r.leadId) });
        },
      },
    );
  };
  return (
    <Modal title={`Skip ${first(r.leadName)}?`} subtitle="Hidden from Revive until they’re in touch again. Nothing is written to your CRM." onClose={onClose} width={460}>
      <div className="revive-reasons" role="radiogroup" aria-label="Reason">
        {[...SKIP_REASONS, 'Other'].map((s) => (
          <button key={s} role="radio" aria-checked={reason === s} className={`pill${reason === s ? ' is-active' : ''}`} onClick={() => setReason(s)}>
            {s}
          </button>
        ))}
      </div>
      {reason === 'Other' && <input className="text-input" value={other} onChange={(e) => setOther(e.target.value)} placeholder="Why skip this lead?" autoFocus />}
      {skip.error && <ErrorBox error={skip.error} />}
      <div className="modal__actions">
        <button className="btn btn--secondary" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn--primary" disabled={!text || skip.isPending} onClick={save}>
          {skip.isPending ? 'Skipping…' : 'Skip lead'}
        </button>
      </div>
    </Modal>
  );
}

function Stat({ label, value, hint }: { label: string; value: string | null; hint?: string }) {
  return (
    <section className="card stat-card">
      <span className="stat-card__label">{label}</span>
      {value == null ? <Skeleton height={36} width={140} /> : <span className="stat-card__value">{value}</span>}
      {hint && <span className="card-meta">{hint}</span>}
    </section>
  );
}

const first = (name: string) => name.split(' ')[0] ?? name;

/** The reason shown in the Rescue queue and written to the CRM note. */
function reviveNote(r: ReviveRowDto): string {
  const ask = r.missing.length ? ` Ask about: ${r.missing.join(', ').toLowerCase()}.` : '';
  const days = Math.max(1, Math.round(quietDays(r)));
  const quiet = `Quiet for ${days} day${days === 1 ? '' : 's'}${r.milestone ? ` after reaching ${r.milestone}` : ''}.`;
  return `${r.reason ? `${quiet} ${r.reason}` : quiet}${ask}`;
}
